import { useQuery } from "@apollo/client";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Typography,
} from "@mui/material";
import { GraphQLFormattedError } from "graphql";
import { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";

import {
  DeescalateThreadsMutation,
  DeescalateThreadsMutationVariables,
  ListSubaccountEscalationAndGeneralInboxesQuery,
  ListSubaccountEscalationAndGeneralInboxesQueryVariables,
  MarkThreadsNotNeedsResponseBulkMutation,
  MarkThreadsNotNeedsResponseBulkMutationVariables,
  MarkThreadsOptedOutBulkMutation,
  MarkThreadsOptedOutBulkMutationVariables,
  MoveThreadsToInboxMutation,
  MoveThreadsToInboxMutationVariables,
  ThreadFieldsForListFragment,
} from "~frontend/__generated__/graphql";
import { FlexBox } from "~frontend/components/FlexBox";
import TooltipButton from "~frontend/components/TooltipButton";
import Select from "~frontend/components/forms/FormSelect";
import SubmitButton from "~frontend/components/forms/SubmitButton";
import { LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES } from "~frontend/graphql/inboxes";
import {
  DEESCALATE_THREADS_BULK,
  GET_THREAD,
  MARK_THREADS_NOT_NEEDS_RESPONSE_BULK,
  MARK_THREADS_OPTED_OUT_BULK,
  MOVE_THREADS_TO_INBOX,
  SEARCH_THREADS,
  SEARCH_THREADS_COUNT,
} from "~frontend/graphql/threads";
import { useSubaccount } from "~frontend/providers/SubaccountProvider";
import { palette } from "~frontend/theme";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import useMutation from "~frontend/util/useMutation";

type Action =
  | { type: "markNotNeedsResponse" }
  | { type: "optOut" }
  | { type: "deescalate" }
  | { type: "moveInbox"; inboxId: string };

export default function BulkActionDialog({
  open,
  selectedThreads,
  onCloseModal,
  onCompleteAction,
}: {
  open: boolean;
  selectedThreads: ThreadFieldsForListFragment[];
  onCloseModal: () => void;
  onCompleteAction: () => void;
}): ReactElement {
  const { subaccount } = useSubaccount()!;
  const formatError = useErrorMessageFormatter();

  const [action, setAction] = useState<null | Action>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const listInboxes = useQuery<
    ListSubaccountEscalationAndGeneralInboxesQuery,
    ListSubaccountEscalationAndGeneralInboxesQueryVariables
  >(LIST_SUBACCOUNT_ESCALATION_AND_GENERAL_INBOXES, {
    variables: { subaccountId: subaccount.id },
  });

  const refetchOptions = {
    refetchQueries: [GET_THREAD, SEARCH_THREADS, SEARCH_THREADS_COUNT],
    awaitRefetchQueries: true,
  };
  const [markThreadsNotNeedsResponseBulk] = useMutation<
    MarkThreadsNotNeedsResponseBulkMutation,
    MarkThreadsNotNeedsResponseBulkMutationVariables
  >(MARK_THREADS_NOT_NEEDS_RESPONSE_BULK, refetchOptions);

  const [markThreadsOptedOutBulk] = useMutation<
    MarkThreadsOptedOutBulkMutation,
    MarkThreadsOptedOutBulkMutationVariables
  >(MARK_THREADS_OPTED_OUT_BULK, refetchOptions);

  const [moveThreadsToInboxBulk] = useMutation<
    MoveThreadsToInboxMutation,
    MoveThreadsToInboxMutationVariables
  >(MOVE_THREADS_TO_INBOX, refetchOptions);
  const [deescalateThreadsBulk] = useMutation<
    DeescalateThreadsMutation,
    DeescalateThreadsMutationVariables
  >(DEESCALATE_THREADS_BULK, refetchOptions);

  const threadsToOptOut = selectedThreads.filter(thread => !thread.isOptedOut);

  const threadsToMarkNotNeedsResponse = selectedThreads.filter(
    thread => thread.defaultContextCampaign && thread.needsTexterResponse
  );

  const threadsToDeescalate = selectedThreads.filter(
    thread => thread.pinnedToInbox
  );

  function handleClose() {
    setAction(null);
    onCloseModal();
  }

  async function handleSubmit() {
    setSubmitting(true);

    if (!action) {
      // not reachable
      return;
    }

    async function handleAction(
      action: Action
    ): Promise<undefined | readonly GraphQLFormattedError[]> {
      switch (action.type) {
        case "markNotNeedsResponse":
          return (
            await markThreadsNotNeedsResponseBulk({
              variables: {
                input: {
                  subaccountId: subaccount.id,
                  threadIds: threadsToMarkNotNeedsResponse.map(
                    thread => thread.id
                  ),
                },
              },
            })
          ).errors;
        case "optOut":
          return (
            await markThreadsOptedOutBulk({
              variables: {
                input: {
                  subaccountId: subaccount.id,
                  threadAndCampaignIds: threadsToOptOut.map(thread => ({
                    threadId: thread.id,
                    campaignId: thread.defaultContextCampaign?.id ?? null,
                  })),
                },
              },
            })
          ).errors;

        case "deescalate":
          return (
            await deescalateThreadsBulk({
              variables: {
                input: {
                  subaccountId: subaccount.id,
                  threadIds: threadsToDeescalate.map(thread => thread.id),
                },
              },
            })
          ).errors;
        case "moveInbox":
          return (
            await moveThreadsToInboxBulk({
              variables: {
                input: {
                  threadIds: selectedThreads.map(thread => thread.id),
                  inboxId: action?.inboxId,
                },
              },
            })
          ).errors;
      }
    }

    const errors = await handleAction(action);

    if (errors) {
      console.error(errors);
      setSubmitError(formatError(errors));
    } else {
      setAction(null);
      onCompleteAction();
    }

    setSubmitting(false);
  }

  const willDeescalateThreads =
    action?.type === "moveInbox" &&
    threadsToDeescalate.length &&
    listInboxes.data?.listSubaccountGeneralInboxes.find(
      i => i.id === action.inboxId
    );

  let submitText: ReactElement;

  if (action?.type === "moveInbox") {
    if (
      listInboxes.data?.listSubaccountEscalationInboxes?.find(
        i => i.id === action.inboxId
      )
    ) {
      submitText = (
        <FormattedMessage
          defaultMessage="Escalate threads"
          description="Submit message when threads will be moved to an escalation inbox"
        />
      );
    } else {
      if (willDeescalateThreads) {
        submitText = (
          <FormattedMessage
            defaultMessage="Move and de-escalate threads"
            description="Message when threads will be moved and automatically de-escalated"
          />
        );
      } else {
        submitText = (
          <FormattedMessage
            defaultMessage="Move threads"
            description="Message when threads will be moved and none will be de-escalated"
          />
        );
      }
    }
  } else {
    submitText = (
      <FormattedMessage
        defaultMessage="Save Changes"
        description="Submit button for bulk action dialog"
      />
    );
  }

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <FormattedMessage
          defaultMessage="Bulk Action on"
          description="Start of header for bulk action dialog"
        />{" "}
        <FormattedMessage
          defaultMessage={`{
            nThreads,
            plural,
            =1 {1 Thread} other {# Threads}
          }`}
          description="header for bulk action display of thread count"
          values={{
            nThreads: selectedThreads.length,
          }}
        />
      </DialogTitle>
      <DialogContent>
        {submitError && <Alert severity="error">{submitError}</Alert>}
        <TooltipButton
          disabled={threadsToMarkNotNeedsResponse.length === 0}
          tooltipText={
            threadsToMarkNotNeedsResponse.length === 0 ? (
              <FormattedMessage
                defaultMessage="No threads to mark as not needing response"
                description="Tooltip for disabled bulk action dialog option when there are no threads to mark as not needing a response"
              />
            ) : undefined
          }
          sx={{ marginBottom: 1 }}
          fullWidth
          variant={
            action?.type === "markNotNeedsResponse" ? "contained" : "outlined"
          }
          color="secondary"
          onClick={() =>
            setAction(
              action?.type === "markNotNeedsResponse"
                ? null
                : { type: "markNotNeedsResponse" }
            )
          }
        >
          <FormattedMessage
            defaultMessage="Mark as not needing response"
            description="Bulk action dialog option for marking threads as not needing a response"
          />
        </TooltipButton>
        <FlexBox gap={1}>
          <TooltipButton
            disabled={threadsToDeescalate.length === 0}
            tooltipText={
              threadsToDeescalate.length === 0 ? (
                <FormattedMessage
                  defaultMessage="No threads to deescalate"
                  description="Tooltip for bulk thread action dialog when there are no threads to deescalate"
                />
              ) : undefined
            }
            sx={{ marginBottom: 1 }}
            fullWidth
            variant={action?.type === "deescalate" ? "contained" : "outlined"}
            color="secondary"
            onClick={() =>
              setAction(
                action?.type === "deescalate" ? null : { type: "deescalate" }
              )
            }
          >
            <FormattedMessage
              defaultMessage="De-escalate ({
            nThreads,
            plural,
            =1 {1 thread} other {# threads}
            })"
              description="Text for deescalate button with number of threads"
              values={{
                nThreads: threadsToDeescalate.length,
              }}
            />
          </TooltipButton>
          <TooltipButton
            disabled={threadsToOptOut.length === 0}
            tooltipText={
              threadsToOptOut.length === 0 ? (
                <FormattedMessage
                  defaultMessage="No threads to Opt Out"
                  description="Tooltip for disabled bulk action dialog option when there are no threads to Opt Out"
                />
              ) : undefined
            }
            sx={{ marginBottom: 1 }}
            fullWidth
            variant={action?.type === "optOut" ? "contained" : "outlined"}
            color="error"
            onClick={() =>
              setAction(action?.type === "optOut" ? null : { type: "optOut" })
            }
          >
            <FormattedMessage
              defaultMessage="Opt Out"
              description="Bulk action dialog option for opting out threads"
            />
          </TooltipButton>
        </FlexBox>
        <Select
          fullWidth
          label="Move threads to a new inbox"
          withNoneOption
          onChange={event => {
            if (event.target.value) {
              setAction({ type: "moveInbox", inboxId: event.target.value });
            } else {
              setAction(null);
            }
          }}
        >
          {[
            ...(listInboxes.data?.listSubaccountEscalationInboxes ?? []).map(
              inbox => (
                <MenuItem key={inbox.id} value={inbox.id}>
                  <Box>
                    <Chip
                      sx={{
                        height: "15px",
                        borderRadius: 0,
                        backgroundColor: palette.lightOrange,
                        margin: 0,
                        "& .MuiChip-label": { padding: "0 4px" },
                      }}
                      label={
                        <Typography variant="h4Caps" component="span">
                          <FormattedMessage
                            defaultMessage="ESCALATION"
                            description="Escalation inbox chip"
                          />
                        </Typography>
                      }
                    />{" "}
                    {inbox.name}
                  </Box>
                </MenuItem>
              )
            ),
            ...(listInboxes.data?.listSubaccountGeneralInboxes ?? []).map(
              inbox => (
                <MenuItem key={inbox.id} value={inbox.id}>
                  {inbox.name}
                </MenuItem>
              )
            ),
          ]}
        </Select>
        {action?.type === "optOut" &&
        threadsToOptOut.length !== selectedThreads.length ? (
          <Typography>
            <FormattedMessage
              defaultMessage="Some threads in your selection are already opted out. This action will only be applied to threads that are not opted out."
              description="Warning when bulk-opting-out threads where some are already opted out"
            />
          </Typography>
        ) : null}
        {action?.type === "markNotNeedsResponse" &&
        threadsToMarkNotNeedsResponse.length !== selectedThreads.length ? (
          <Typography>
            <FormattedMessage
              defaultMessage="Some threads in your selection are already marked as not needing a response. This action will only be applied to threads that need a response."
              description="Warning when bulk-marking threads as not needing a response where some are already marked as not needing a response"
            />
          </Typography>
        ) : null}
        {willDeescalateThreads ? (
          <Alert severity="warning">
            <strong>
              <FormattedMessage
                defaultMessage="Note:"
                description="Note label"
              />
            </strong>{" "}
            <FormattedMessage
              defaultMessage="{nThreads} of the threads you have selected {nThreads, plural, =1 {is} other {are}} currently in an escalation inbox. Moving threads to a new inbox will automatically de-escalate them."
              description="Warning about automatically deescalating moved threads"
              values={{
                nThreads: threadsToDeescalate.length,
              }}
            />
          </Alert>
        ) : undefined}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} color="secondary">
          <FormattedMessage
            defaultMessage="Cancel"
            description="Cancel button for bulk action dialog"
          />
        </Button>
        <SubmitButton
          isSubmitting={submitting}
          onClick={handleSubmit}
          variant="contained"
          color="primary"
          disabled={action == null}
          submitText={submitText}
          loadingText={
            <FormattedMessage
              defaultMessage="Saving Changes..."
              description="Submit button saving state for bulk action dialog"
            />
          }
        />
      </DialogActions>
    </Dialog>
  );
}
